import { StyleSheet, Text, type TextProps } from "react-native";

import {
  fonts,
  lightColors,
  palette,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { TextSize, TextWeight } from "./types";

export function AppText({
  size = "sm",
  weight,
  tone = "default",
  style,
  ...props
}: TextProps & {
  size?: TextSize;
  weight?: TextWeight;
  tone?: "default" | "muted" | "danger" | "inverse" | "link";
}) {
  const { stylesheet, sizeStyles, weightStyles } = useThemedStyles(makeStyles);
  return (
    <Text
      style={[
        sizeStyles[size],
        weight ? weightStyles[weight] : null,
        tone === "muted" ? stylesheet.muted : null,
        tone === "danger" ? stylesheet.danger : null,
        tone === "inverse" ? stylesheet.inverse : null,
        tone === "link" ? stylesheet.link : null,
        style,
      ]}
      {...props}
    />
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    xs: { color: colors.foreground, fontFamily: fonts.regular, fontSize: 12 },
    sm: { color: colors.foreground, fontFamily: fonts.regular, fontSize: 14 },
    base: { color: colors.foreground, fontFamily: fonts.regular, fontSize: 16 },
    lg: { color: colors.foreground, fontFamily: fonts.regular, fontSize: 18 },
    medium: { fontFamily: fonts.medium },
    semibold: { fontFamily: fonts.semibold },
    bold: { fontFamily: fonts.bold },
    muted: { color: colors.mutedForeground },
    danger: { color: colors.danger },
    // Fixed light text for permanently dark-green surfaces (e.g. AuthScreen)
    // -- must not follow the theme, or dark mode flips it to near-black.
    inverse: { color: lightColors.background },
    link: {
      color: colors.heading,
      textDecorationLine: "underline",
      textDecorationColor: palette.brandMint,
    },
  });
  const sizeStyles = {
    xs: stylesheet.xs,
    sm: stylesheet.sm,
    base: stylesheet.base,
    lg: stylesheet.lg,
  };
  const weightStyles = {
    medium: stylesheet.medium,
    semibold: stylesheet.semibold,
    bold: stylesheet.bold,
  };
  return { stylesheet, sizeStyles, weightStyles };
};
